import { DonationFundPlatformTypeEnum } from '@graphql/generated-base';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { getCurrencySymbolFromCurrencyCode } from '@shared/utils/currency/utils';
import type { AddCashFundDialogFields } from '..';
import type { PaymentMethodDisclaimerSignedMetadata } from '../AppPaymentDisclaimerDialog.controller';
import type { DonationFundInfoFragmentWithUrl } from '../../Catalog/components/ProductGrid/ProductGrid.controller';

export const normalizePaymentUsername = (paymentMethod?: Maybe<DonationFundPlatformTypeEnum>, username?: Maybe<string>): string => {
  if (paymentMethod === DonationFundPlatformTypeEnum.cashapp) {
    return (username ?? '').replace(/^\$+/, '');
  }
  return username ?? '';
};

export const getPaymentLink = (paymentMethod?: Maybe<DonationFundPlatformTypeEnum>, username?: Maybe<string>) => {
  switch (paymentMethod) {
    case DonationFundPlatformTypeEnum.cashapp:
      // registry_service builds the platform link as cash.app/$<username> and rejects a fund url that differs
      return `https://cash.app/$${normalizePaymentUsername(paymentMethod, username)}`;
    case DonationFundPlatformTypeEnum.paypal:
      return `https://paypal.me/${username}`;
    case DonationFundPlatformTypeEnum.venmo:
      return `https://venmo.com/u/${username}`;
    default:
      return '';
  }
};

export const getPaymentUsernameFormat = (paymentMethod?: Maybe<DonationFundPlatformTypeEnum>, currencyCode?: string) => {
  return `${paymentMethod === DonationFundPlatformTypeEnum.cashapp ? getCurrencySymbolFromCurrencyCode(currencyCode || DEFAULT_CURRENCY) : '@'}`;
};

type paymentData = Pick<AddCashFundDialogFields, 'paymentMethod' | 'username'>;

export const createPaymentMetadata = (values: paymentData): PaymentMethodDisclaimerSignedMetadata => ({
  paymentLink: getPaymentLink(values.paymentMethod, values.username),
  paymentMethod: values.paymentMethod as string,
  paymentUsername: values.username!,
});

export const getExistingPlatform = (values: paymentData, donationFundInfo: DonationFundInfoFragmentWithUrl | undefined) => {
  return donationFundInfo?.platforms.some(platform => platform.type === values.paymentMethod && platform.username === values.username);
};

export const findDonationFundPlatform = (
  values: { paymentMethod?: Maybe<DonationFundPlatformTypeEnum>; username?: Maybe<string> },
  paymentLink: string,
  donationFundInfo: DonationFundInfoFragmentWithUrl | undefined,
) => {
  return donationFundInfo?.platforms.find(platform => platform.type === values.paymentMethod && platform.username === values.username && platform.link === paymentLink);
};

/**
 * Resolves the already-saved platform a set of form values maps onto. The disclaimer gate and the
 * submit path must agree on this, otherwise a host signs a disclaimer for one platform while the
 * mutation creates another.
 */
export const resolveExistingDonationFundPlatform = (
  values: Pick<AddCashFundDialogFields, 'paymentMethod' | 'username' | 'enableAppPlatform' | 'isLinkToCharitySelected'>,
  donationFundInfo: DonationFundInfoFragmentWithUrl | undefined,
) => {
  if (values.isLinkToCharitySelected && !values.enableAppPlatform) {
    return null;
  }
  const normalizedUsername = normalizePaymentUsername(values.paymentMethod, values.username);
  return findDonationFundPlatform(
    { paymentMethod: values.paymentMethod, username: normalizedUsername },
    getPaymentLink(values.paymentMethod, normalizedUsername),
    donationFundInfo,
  );
};
